import { forwardRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface BatchComboboxOption {
  id: number;
  name: string;
}

interface BatchComboboxProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "value" | "onSelect" | "className"> {
  batches: BatchComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  testId?: string;
  disabled?: boolean;
  // Optional richer item content (e.g. AddKid's day/time/frequency line)
  // while `name` still drives search matching and the trigger's own label.
  renderOption?: (batch: BatchComboboxOption) => ReactNode;
}

// Replaces the raw Select-based batch pickers (AssignBatchDialog,
// CoachKidDetail, schedule-session-dialog, AddKid) — those all share the
// shadcn/Radix Select's popper-positioned Viewport, whose scroll area is
// bound to `--radix-select-trigger-height` and only scrolls reliably via
// its small hover-driven arrow buttons. Fine for a handful of items, broken
// on touch once the list is long (confirmed live with 16-17 real batches:
// the viewport genuinely overflows but normal scroll input doesn't move it
// — see the dropdown-scroll investigation). cmdk's CommandList underneath
// this component uses a plain bounded-height `overflow-y-auto` div, not
// tied to the trigger's size, plus type-to-filter so a long list is never
// fully scrolled through in the first place. Same pattern already proven
// in BatchesPage.tsx's player-search dialog.
//
// forwardRef + spreading `...rest` onto the trigger Button lets this drop
// into react-hook-form's <FormControl> exactly like the date-picker Buttons
// elsewhere in schedule-session-dialog.tsx — FormControl's Slot injects
// id/aria-describedby/aria-invalid onto its single child, which only lands
// correctly if that child forwards a ref and spreads unknown props.
export const BatchCombobox = forwardRef<HTMLButtonElement, BatchComboboxProps>(function BatchCombobox(
  { batches, value, onValueChange, placeholder = "Select a batch", className, testId, disabled, renderOption, ...rest },
  ref,
) {
  const [open, setOpen] = useState(false);
  const selected = batches.find((b) => String(b.id) === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
          {...rest}
        >
          <span className="truncate">{selected ? selected.name : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search batches…" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty>No batches found.</CommandEmpty>
            <CommandGroup>
              {batches.map((b) => (
                <CommandItem
                  key={b.id}
                  value={b.name}
                  data-testid={testId ? `${testId}-option-${b.id}` : undefined}
                  onSelect={() => {
                    onValueChange(String(b.id));
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      String(b.id) === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {renderOption ? renderOption(b) : b.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});
